import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';
import { insertEmbryos, deleteUntransferredEmbryosOfDonor } from './embryos';

/**
 * Id da doadora da FIV, gerado na tela e definitivo desde o primeiro instante.
 *
 * Era um `temp-…`, e o banco trocava por um UUID novo a CADA gravação sem a tela
 * ficar sabendo: o segundo "Salvar" não achava a doadora e criava outra, e a
 * que saía levava os embriões para o limbo — órfãos, sem doadora, mas ainda
 * segurando o número. No 2026-0053 foram quatro gravações num minuto: 168
 * embriões fantasmas nos números 1–168, e a FIV de verdade começando no 169.
 *
 * Com o id certo desde o início, gravar de novo é update.
 */
export const newFivDonorId = () => crypto.randomUUID();

/** Sessão de FIV de um serviço, ou null se ainda não existe. */
export const fetchFivSession = (serviceId) =>
  cached(`fiv-session:${serviceId}`, () => run(supabase
    .from('fiv_sessions')
    .select('*')
    .eq('service_id', serviceId)
    .maybeSingle()));

/** Doadoras da sessão, na ordem em que foram lançadas. */
export const fetchFivDonors = (sessionId) =>
  cached(`fiv-donors:${sessionId}`, () => run(supabase
    .from('fiv_donors')
    .select('*')
    .eq('fiv_session_id', sessionId)
    .order('sort_order')));

/**
 * Caminho principal de gravação da FIV.
 *
 * O RPC recebe o estado inteiro da sessão e reconcilia do lado do banco, numa
 * transação só. É declarativo — reexecutar produz o mesmo resultado —, o que
 * mais adiante permite reenviar da fila offline sem duplicar embrião.
 */
export const saveFivSession = queueable('fiv.save',
  (params) => run(supabase.rpc('save_fiv_session', params)),
  { pendingOf: (params, appData) => appData && {
      kind: 'fiv', id: params.p_service_id, data: appData, label: 'Sessão de FIV',
    } });

// ── Caminho sem produção lançada ──────────────────────────────────────────
// Escritas linha a linha, sem transação.
//
// Nasceu para a tela de FIV simplificada, que lançava embrião a embrião; essa
// tela não existe mais. O que sobrou de gatilho é a FIV que ainda não produziu
// nada: sem nenhuma contagem por estágio e sem nenhum destino, o RPC acima não
// tem o que reconciliar, e este caminho grava a sessão e as doadoras para que
// o lançamento continue de onde parou.
//
// Ele NÃO opina sobre estágios e destinos: essas colunas ficam fora do payload
// justamente porque este caminho não sabe nada sobre elas.

/**
 * Grava a FIV pelo caminho legado como UMA operação.
 *
 * Eram cinco escritas soltas no hook, encadeadas pelo id que a primeira
 * devolvia. Offline isso quebrava por inteiro: a primeira ia para a fila e
 * devolvia um marcador, não um id, e todo o resto trabalhava em cima do
 * marcador. E é justamente a aba mobile que usa este caminho — a que mais roda
 * sem sinal.
 *
 * As doadoras chegam SEM `fiv_session_id`, pelo mesmo motivo de OPU e DG: o id
 * da sessão só existe no instante em que a operação roda de verdade.
 */
export const saveFivSessionLegacy = queueable('fiv.save-legado',
  async (sessionPayload, donorRows, embrioesPorDoadora, laboratoryId) => {
    const sessionId = await upsertFivSessionRow(sessionPayload);

    if (!donorRows || donorRows.length === 0) {
      await deleteAllFivDonors(sessionId);
      return { sessionId };
    }

    const comSessao = donorRows.map((d) => ({ ...d, fiv_session_id: sessionId }));
    await upsertFivDonors(comSessao);
    await deleteFivDonorsNotIn(sessionId, comSessao.map((d) => d.id));

    for (const { fivDonorId, linhas } of (embrioesPorDoadora || [])) {
      // Protege embriões já transferidos, mesmo sem receptora vinculada.
      await deleteUntransferredEmbryosOfDonor(fivDonorId, laboratoryId);
      if (linhas.length > 0) await insertEmbryos(linhas);
    }

    return { sessionId };
  },
  {
    pendingOf: (sessionPayload, _d, _e, _lab, appData) => appData && {
      kind: 'fiv', id: sessionPayload.service_id, data: appData, label: 'Sessão de FIV',
    },
  });

export const upsertFivSessionRow = async (payload) => {
  const saved = await run(supabase
    .from('fiv_sessions')
    .upsert(payload, { onConflict: 'service_id' })
    .select('id')
    .single());
  return saved.id;
};

export const upsertFivDonors = (rows) =>
  run(supabase.from('fiv_donors').upsert(rows, { onConflict: 'id' }));

/** Remove as doadoras da sessão que não estão mais na lista. */
export const deleteFivDonorsNotIn = (sessionId, keepIds) =>
  run(supabase.from('fiv_donors').delete()
    .eq('fiv_session_id', sessionId)
    .not('id', 'in', `(${keepIds.map(id => `"${id}"`).join(',')})`));

export const deleteAllFivDonors = (sessionId) =>
  run(supabase.from('fiv_donors').delete().eq('fiv_session_id', sessionId));
